import { useCallback, useState } from 'react';

import { useAuth } from '../auth';
import { FriendPanel } from '../friends';
import type { MegaWorldId } from '../generated/MegaWorldId';
import type { ClaimMetaJson } from '../platform/api';
import { useEmbark } from '../platform/store';
import { MegaWorldPicker } from './MegaWorldPicker';
import { MyWorlds } from './MyWorlds';
import { WorldMap } from './WorldMap';
import styles from './embark.module.css';

type Screen = 'my-worlds' | 'pick-world' | 'map';

export function EmbarkFlow(): JSX.Element {
  const [screen, setScreen] = useState<Screen>('my-worlds');
  const selectMegaWorld = useEmbark((s) => s.selectMegaWorld);
  const selectedMegaWorld = useEmbark((s) => s.selectedMegaWorld);
  const enterExpansion = useEmbark((s) => s.enterExpansion);
  const displayName = useAuth((s) => s.displayName);
  const discriminator = useAuth((s) => s.discriminator);
  const logout = useAuth((s) => s.logout);

  const handleSelectMegaWorld = (id: MegaWorldId) => {
    selectMegaWorld(id);
    setScreen('map');
  };

  const handleBack = () => {
    if (screen === 'map') setScreen('pick-world');
    else if (screen === 'pick-world') setScreen('my-worlds');
  };

  const handleExpand = useCallback(
    (claim: ClaimMetaJson) => {
      selectMegaWorld(claim.mega_world);
      setScreen('map');
      setTimeout(() => void enterExpansion(claim.claim_id), 100);
    },
    [selectMegaWorld, enterExpansion],
  );

  return (
    <div className={styles.embarkRoot}>
      <div className={styles.header}>
        {screen !== 'my-worlds' && (
          <button className={styles.backBtn} onClick={handleBack}>
            &larr; Back
          </button>
        )}
        <span className={styles.headerTitle}>MyTile</span>
        {screen === 'map' && selectedMegaWorld && (
          <span style={{ color: '#949ba4', fontSize: 13 }}>
            {selectedMegaWorld}
          </span>
        )}
        <span className={styles.headerPlayer}>
          {displayName
            ? `${displayName}${discriminator != null ? `#${String(discriminator).padStart(4, '0')}` : ''}`
            : 'Player'}
          <button
            onClick={logout}
            style={{
              background: 'none',
              border: 'none',
              color: '#949ba4',
              cursor: 'pointer',
              fontSize: 12,
              marginLeft: 12,
            }}
          >
            Sign out
          </button>
        </span>
      </div>

      <div className={styles.embarkBody}>
        <div className={styles.embarkMain}>
          {screen === 'my-worlds' && (
            <MyWorlds
              onCreateNew={() => setScreen('pick-world')}
              onExpand={handleExpand}
            />
          )}
          {screen === 'pick-world' && (
            <MegaWorldPicker onSelect={handleSelectMegaWorld} />
          )}
          {screen === 'map' && <WorldMap />}
        </div>
        {screen === 'my-worlds' && <FriendPanel />}
      </div>
    </div>
  );
}
